import { useEffect, useState } from "react";
import "./paneles.css";
import { useDialogo } from "./dialogo";
import { escalaGuardada, ponerEscala, siguienteEscala } from "./tamano-de-letra";
import { AnadirOrdenador, ExtrasDeMaquina, Historial, type ApiDeAgentes, type Panel } from "./Workers";
import { AvisarmeEnEsteMovil } from "./AvisarmeEnEsteMovil";

/**
 * El panel de control: ¿está todo funcionando?
 *
 * "Hoy" responde a otra cosa —qué tengo encima— y por eso son dos pantallas.
 * Cuando algo va mal, la primera pregunta no es qué tarea ha fallado sino
 * dónde está el fallo: en la máquina, en la cola o en la base. Esta pantalla
 * responde eso de un vistazo y sin bajar.
 *
 * Lo que no se mide, no se pinta. Al final hay una lista de lo que se pidió y
 * no existe, con el motivo: una luz verde falsa es peor que un hueco, porque
 * el hueco se pregunta y la luz se cree.
 */

interface Cerebro {
  proveedor: "anthropic" | "deepseek" | "openai" | "together";
  /** El que habla contigo. El mismo en las cuatro marchas. */
  modeloDirector: string;
  /** El de la fontanería de fondo: clasificar, extraer, titular. */
  modeloEconomico: string;
  modeloVerificador: string;
  /** Un modelo distinto en una marcha concreta. Vacío = el del director. */
  modeloPorMarcha: Record<string, string | null>;
  esfuerzoChat: string;
  esfuerzoLigero: string;
  esfuerzoComplejo: string;
  esfuerzoExcepcional: string;
  modo: "automatico" | "manual";
  nivelManual: string;
}

interface Trabajo {
  jobId: string;
  titulo: string;
  capacidad: string;
  agente: string | null;
  paso: string | null;
  hechos: number | null;
  deCuantos: number | null;
  porcentaje: number | null;
  segundos: number | null;
  sinAvanzarSegundos: number | null;
  intentos: number;
}

interface Ordenador {
  id: string;
  nombre: string;
  hostname: string | null;
  estado: "trabajando" | "libre" | "sin-senal" | "apagado";
  /** Si lleva más de un mes callada: desde cuándo, y cuántos días. */
  apagadaDesde: string | null;
  diasApagada: number | null;
  conectada: boolean;
  segundosDesdeLatido: number | null;
  especialidad: string | null;
  plataforma: string | null;
  cpu: string | null;
  nucleos: number | null;
  memoriaMb: number | null;
  /** Lo que tiene ahora. Null es «no lo sé», nunca cero. */
  memoriaLibreMb: number | null;
  discoLibreGb: number | null;
  discoTotalGb: number | null;
  carga: number | null;
  version: string | null;
  /** `true` al día, `false` por detrás, `null` cuando no se puede saber. */
  alDia: boolean | null;
  loDeLaVersion: string;
  capacidades: string[];
  aLaVez: number | null;
  completados: number | null;
  trabajando: Trabajo[];
}

/**
 * Una regla de la casa (Bloque 5): una instrucción que se le da a BilboBot sin
 * tocar el código. La escribe él y la enciende Jorge aprobándola.
 */
interface ReglaDeLaCasa {
  id: string;
  titulo: string;
  /** `siempre`, o el nombre de un grupo de herramientas. */
  ambito: string;
  cuando: string;
  entonces: string;
  estado: string;
  fallos: number;
  apagadaEn: string | null;
  apagadaPorQue: string | null;
}

interface Control {
  sistema: {
    baseDeDatos: { ok: boolean; latenciaMs: number };
    director: { vivo: boolean; arrancadoHaceSegundos: number };
    cerebro: Cerebro & {
      pegas: string[];
      catalogo: { proveedor: string; modelo: string; etiqueta: string; nota: string }[];
      esfuerzos: { valor: string; etiqueta: string; nota: string }[];
      niveles: { valor: string; campo: keyof Cerebro; titulo: string; ejemplo: string }[];
      /** Lo que viaja de verdad en la petición. Sale del mismo código que la hace. */
      ficha: {
        marchas: {
          nivel: string;
          titulo: string;
          modelo: string;
          esfuerzo: string;
          envia: Record<string, unknown>;
        }[];
        fondo: { modelo: string; esfuerzo: string; envia: Record<string, unknown> };
        verificador: { modelo: string; esfuerzo: string };
        siempre: { que: string; valor: string }[];
      };
      claves: Record<string, boolean>;
    };
    colas: {
      esperando: number;
      enMarcha: number;
      fallidosHoy: number;
    };
    reparto: {
      pausado: boolean;
      motivo: string | null;
      motivoDelVeto: string | null;
      vetadas: string[];
    };
    gasto: { mesEur: number; presupuestoEur: number };
    consumoIa: {
      proveedor: string;
      modelo: string;
      llamadas: number;
      cacheHit: number;
      cacheMiss: number;
      porcentajeCache: number | null;
      salida: number;
      razonamiento: number;
      eur: number;
      segundosMedio: number;
      herramientas: number;
      fallos: number;
    }[];
    loQueNoSeMide: { que: string; porque: string }[];
  };
  ordenadores: Ordenador[];
  reglas: ReglaDeLaCasa[];
  errores: {
    jobId: string;
    capacidad: string;
    error: string | null;
    cuando: string | null;
    intentos: number;
    worker: string | null;
  }[];
}

/** Cómo se llaman de cara a ti, no como los llama su API. */
const NOMBRE_PROVEEDOR: Record<string, string> = {
  deepseek: "DeepSeek",
  openai: "OpenAI",
  anthropic: "Anthropic",
  together: "Together AI",
};

const ESTADOS: Record<Ordenador["estado"], { texto: string; clase: string }> = {
  trabajando: { texto: "Trabajando", clase: "trabajando" },
  libre: { texto: "Disponible", clase: "libre" },
  "sin-senal": { texto: "Sin señal", clase: "sin-senal" },
  /*
   * Apagada no es «sin señal»: «sin señal» es que se ha caído ahora y puede
   * volver, y eso hay que mirarlo. Con un mes callada, mirarlo no lleva a
   * ninguna parte — y tres de cinco siempre en rojo enseñan a no mirar.
   */
  apagado: { texto: "Apagado", clase: "apagado" },
};

/** «Apagado desde el 13 de agosto», que es lo que hace falta saber. */
function desdeCuandoApagada(o: Ordenador): string {
  if (!o.apagadaDesde) return "Nunca ha llegado a conectarse";
  const cuando = new Date(o.apagadaDesde).toLocaleDateString("es-ES", {
    day: "numeric",
    month: "long",
  });
  return `Apagado desde el ${cuando}${o.diasApagada === null ? "" : ` (${o.diasApagada} días)`}`;
}

/** 128400 → «128,4 k»: ocho cifras en una celda no se leen. */
function miles(n: number): string {
  if (n < 1_000) return String(n);
  if (n < 1_000_000) return `${(n / 1_000).toFixed(1)} k`;
  return `${(n / 1_000_000).toFixed(2)} M`;
}

/** «hace 4 min». Lo que delata a un vigía muerto. */
function hace(cuando: string): string {
  const segundos = Math.round((Date.now() - new Date(cuando).getTime()) / 1000);
  if (segundos < 90) return `hace ${segundos} s`;
  const minutos = Math.round(segundos / 60);
  if (minutos < 90) return `hace ${minutos} min`;
  const horas = Math.round(minutos / 60);
  if (horas < 48) return `hace ${horas} h`;
  return `hace ${Math.round(horas / 24)} días`;
}

function duracion(segundos: number | null): string {
  if (segundos === null) return "";
  if (segundos < 60) return `${segundos} s`;
  const m = Math.floor(segundos / 60);
  if (m < 60) return `${m} min`;
  return `${Math.floor(m / 60)} h ${m % 60} min`;
}

/**
 * Las cuatro caras del panel.
 *
 * Estaba todo en una columna y había que bajar tres pantallas para llegar al
 * consumo. Son cuatro preguntas distintas —¿va todo?, ¿con qué piensa?, ¿qué
 * cuesta?, ¿qué ha fallado?— y cada una se hace en un momento distinto: verlas
 * a la vez no ayuda, sólo obliga a buscar.
 */
interface Vuelta {
  cuando: string;
  duracionMs: number;
  servidor: {
    memoria: {
      totalMb: number;
      disponibleMb: number;
      libreMb: number;
      cacheMb: number;
      swapTotalMb: number;
      swapLibreMb: number;
      porcentajeDisponible: number;
    };
    disco: { totalGb: number; libreGb: number; usadoPorCiento: number };
    carga: { uno: number; cinco: number; quince: number; nucleos: number };
    enPieSegundos: number;
    procesos: { nombre: string; mb: number }[];
    /**
     * Si sobreviviría a un reinicio. Se añadió el 27/08/2026, después de que un
     * kernel instalado y nunca arrancado tuviera el servidor cinco horas caído.
     * Opcional porque una vuelta guardada de antes de esa fecha no lo trae.
     */
    arranque?: {
      kernelActual: string;
      kernelProximoArranque: string | null;
      kernelMasNuevoInstalado: string | null;
      proximoReinicioSinProbar: boolean;
      informeDeFalloDeArranque: string | null;
      registroPersistente: boolean;
    };
    avisos: { que: string; detalle: string; gravedad: string }[];
  };
  webs: {
    cliente: string;
    dominio: string;
    conCliente: boolean;
    origen: string;
    esRedireccion: boolean;
    estado: string;
    ms: number;
    detalle: string;
    certificadoCaduca: string | null;
    diasDeCertificado: number | null;
  }[];
  problemas: { huella: string; que: string; detalle: string; gravedad: string }[];
  loQueNoSeVigila: { que: string; porque: string }[];
  /**
   * El blindaje de cada web, y no solo el de las que fallan.
   *
   * Opcional porque una vuelta guardada de antes del 30/08/2026 no lo trae, y
   * el panel tiene que poder pintar una vuelta vieja sin romperse.
   */
  blindaje?: {
    /**
     * Cuando paso el inspector, que no es cuando se monto esta vuelta.
     *
     * Son dos relojes: el inspector pasa cada media hora y la vuelta cada diez
     * minutos. Sin ensenarlo, esto pintaria datos de hace horas con aspecto de
     * recien tomados.
     *
     * Opcional por lo mismo que el resto: una vuelta guardada antes del
     * 31/08/2026 no lo trae.
     */
    cuando?: string | null;
    estados: {
      dominio: string;
      cliente?: string;
      seHaPodidoMirar: boolean;
      porQueNo?: string;
      /** Ya con nombre legible: la definicion manda desde el servidor. */
      faltan?: string[];
      /**
       * Que tiene pendiente de actualizar, ya resumido: «WordPress + 2 plugins».
       *
       * Ausente NO significa «al dia»: significa que no se ha podido mirar. El
       * servidor solo lo pone cuando ha leido el inventario de verdad.
       */
      actualizaciones?: string;
    }[];
    resumen: { enteras: number; conFaltas: number; sinMirar: number; total: number };
    repuesto: string[];
  };
}

/**
 * Los ataques que ha sufrido este servidor, con sus documentos.
 *
 * Escrito a mano y a proposito: son cuatro entradas en la vida de una empresa,
 * no un flujo. Y los documentos viven fuera —se escriben y se actualizan
 * solos—; esto es el indice para llegar a ellos sin buscar en el correo.
 */
/**
 * Lo que se ha hecho, dia a dia.
 *
 * Esto SI puede ir escrito a mano, y es la excepcion: es historia. Lo que se
 * hizo el 27 de agosto no va a cambiar nunca. Lo que no puede escribirse a
 * mano es el ESTADO —si hoy SSH acepta contraseñas, si `almalinux` sigue
 * bloqueada—, porque eso cambia solo y una lista escrita se queda atras sin
 * avisar. Ese estado se mide, y va en otro sitio.
 *
 * El detalle y el porque de cada decision estan en «El parte del ataque».
 * Aqui cabe el titular, para saber que se hizo sin abrir nada.
 */
const LO_HECHO = [
  {
    dia: "26-27 ago",
    titulo: "Echarle, y las cinco horas caido",
    puntos: [
      "143 puertas traseras, 429 nidos y 70 php.ini retirados",
      "65 bucles de bash que reescribian la puerta cada segundo, parados",
      "El servidor no volvio del reinicio: el kernel 503.40.1 no arranca en esta maquina. Cinco horas, 64 webs fuera",
      "Fijado el kernel bueno, y que instalar uno nuevo ya no lo ponga por defecto",
    ],
  },
  {
    dia: "28 ago",
    titulo: "Auditoria y cierre de lo evidente",
    puntos: [
      "Cerrada la base de datos al exterior, y atadas sus cuentas a este servidor",
      "Duplicator eliminado de 64 instalaciones; retirado un archivo publico de 939 MB con la base entera dentro",
      "Bloqueadas las ordenes al sistema: de 2 webs a 35",
      "Descubierto que el atacante borro el plugin de seguridad de 14 webs en 45 minutos ANTES de atacar",
    ],
  },
  {
    dia: "29 ago",
    titulo: "Lo que se habia escapado",
    puntos: [
      "306 .htaccess suyos, en 58 dominios, que reabrian la puerta. No los ve ningun antivirus: no son codigo",
      "54 nidos en webs que no son WordPress, donde ningun barrido miraba",
      "castellana91 se resembraba sola: 8 ficheros del nucleo inyectados",
      "clinicaaroka mandaba cada contraseña tecleada a un servidor de fuera desde el 21 de agosto",
      "Las 6 aplicaciones, sacadas de root. 67 contraseñas de base de datos cambiadas",
    ],
  },
  {
    dia: "30 ago",
    titulo: "Las 70 webs al dia",
    puntos: [
      "Las 50 sin tienda y las 19 tiendas a WordPress 7.1, sin romper ninguna",
      "14 dominios sacados de PHP 7.4: 139 ficheros corregidos, todos con copia previa",
      "1,43 millones de comentarios basura retirados; 3,7 GB recuperados",
      "Elementor: borrada la licencia falsa de 14 webs, que era la causa del descuelgue",
    ],
  },
  {
    dia: "31 ago",
    titulo: "Comprobar que no queda nadie",
    puntos: [
      "Cero intrusos dentro, comprobado en trece frentes distintos",
      "16 piezas de puerta trasera mas en clinicaaroka, y 4 ficheros de phishing bancario desde 2020",
      "Cerrada una fuga: los pedidos de La Gilda se bajaban desde internet, con nombre, CIF y direccion",
      "El vigilante del blindaje, en marcha: 14 comprobaciones en las 70 webs cada media hora",
      "La llave que decia «solo-postgres» era root completo. Partida en dos",
    ],
  },
  {
    dia: "1 sep",
    titulo: "Las contraseñas",
    puntos: [
      "Las 70 de administrador de WordPress, una distinta por web, comprobada contra su propio hash",
      "19 claves de aplicacion rotadas y probadas; 18 credenciales al cofre cifrado",
      "coquisa.es llevaba dos dias muerta devolviendo 200: servia la copia del 30",
      "Las copias las leia cualquier usuario del servidor. Cerradas, y 175 GB liberados",
    ],
  },
  {
    dia: "2 sep",
    titulo: "Tapiar la puerta",
    puntos: [
      "SSH ya no acepta contraseñas: solo llave",
      "`almalinux` bloqueada — entraba con contraseña y era root sin pedir nada mas",
      "Contadas las seis puertas a root, una a una. Cuenta propia en Plesk para el programador",
      "Trello, arreglado tras tres semanas y 1.500 errores",
      "BilboBot llevaba cinco dias sin poder ver ninguna web, y nada fallaba",
    ],
  },
] as const;

const HACKEOS = [
  {
    fecha: "26 ago 2026",
    titulo: "Intrusion con acceso root",
    resumen:
      "Una sola noche. 143 puertas traseras, 429 nidos, 306 .htaccess y 65 bucles que las " +
      "reescribian cada segundo. Antes de atacar borro el plugin de seguridad de 14 webs en " +
      "45 minutos. Sigue sin saberse por donde entro.",
    documentos: [
      {
        nombre: "El parte del ataque",
        url: "https://claude.ai/code/artifact/5833c27a-0939-4510-9a39-1817cd567903",
        que: "el documento vivo: que paso, que se hizo cada dia y que queda",
      },
      {
        nombre: "Que se hizo en cada web",
        url: "https://claude.ai/code/artifact/6bfb2758-e96c-4b17-9f4d-97e5496f4627",
        que: "ficha de las 70 webs, con buscador, para responder a un cliente",
      },
      {
        nombre: "La puerta y la llave maestra",
        url: "https://claude.ai/code/artifact/8822bc95-6bad-4da8-845c-076974bc91e8",
        que: "el blindaje del acceso, paso a paso",
      },
      {
        nombre: "Lo que queda del incidente",
        url: "https://claude.ai/code/artifact/7103143d-7cf6-4481-a799-3e9fdabfa776",
        que: "foto del 27 de agosto; se conserva como registro",
      },
    ],
  },
  {
    fecha: "21 ago 2026",
    titulo: "Capturador de contrasenas en clinicaaroka",
    resumen:
      "Cada acceso al panel de esa web —usuario y contrasena en claro— se enviaba a un " +
      "servidor de fuera. Cinco dias antes del ataque grande, y descubierto ocho dias despues.",
    documentos: [
      {
        nombre: "El parte del ataque",
        url: "https://claude.ai/code/artifact/5833c27a-0939-4510-9a39-1817cd567903",
        que: "apartado «clinicaaroka.com mandaba las contrasenas al atacante»",
      },
    ],
  },
  {
    fecha: "3 ago 2026",
    titulo: "Webshells anteriores, en clinicaaroka",
    resumen:
      "Dieciseis piezas de puerta trasera, y tres webshells disfrazados de pagina 404. " +
      "Veintitres dias antes del ataque que conociamos.",
    documentos: [
      {
        nombre: "Auditoria del 29 de agosto",
        url: "https://claude.ai/code/artifact/dc383f0a-bbbc-4459-970e-2995161aa36d",
        que: "como se encontraron y que hacia cada una",
      },
    ],
  },
  {
    fecha: "oct 2020 - mar 2022",
    titulo: "Phishing bancario, sin relacion con el ataque",
    resumen:
      "Tres webs de clientes llevaban anos sirviendo suplantacion de Chase y Credit Agricole. " +
      "Nadie lo habia visto nunca. Retirado el 31 de agosto.",
    documentos: [
      {
        nombre: "El parte del ataque",
        url: "https://claude.ai/code/artifact/5833c27a-0939-4510-9a39-1817cd567903",
        que: "apartado «phishing desde 2020»",
      },
    ],
  },
] as const;

const CARAS = [
  { id: "sistema", titulo: "Sistema" },
  { id: "vigilancia", titulo: "Vigilancia" },
  { id: "cerebro", titulo: "Cerebro" },
  { id: "consumo", titulo: "Consumo" },
  /*
   * «Incidencias» y no «Fallos» porque aqui caben dos cosas distintas: lo que
   * se rompio solo —un trabajo que no salio— y lo que rompio alguien. Meter un
   * ataque bajo el epigrafe «fallos» lo iguala con un timeout de red, y no es
   * lo mismo ni se mira igual.
   */
  { id: "fallos", titulo: "Incidencias" },
] as const;

type Cara = (typeof CARAS)[number]["id"];

interface EstadoGoogle {
  configurado: boolean;
  conectado: boolean;
  redireccion: string;
  searchConsole?: number;
  analytics?: number;
  falta?: string;
}

/**
 * La cuenta de Google de Jorge: Search Console y Analytics.
 *
 * Se conecta autorizando como él, no con una cuenta de servicio. La razón está
 * escrita en `integrations/google.ts` y se resume en que Search Console no
 * tiene forma de dar permisos por API: habría que añadir la cuenta de servicio
 * a mano en cada propiedad, y acordarse otra vez con cada web nueva.
 *
 * Enseña CUÁNTAS propiedades ve de cada sitio, no un «conectado» a secas. Un
 * verde que no dice cuántas es un verde que no se puede comprobar: si mañana
 * pasa de 30 a 2, eso hay que poder verlo.
 */
function GooglePanel() {
  const { preguntar, avisar, dialogo } = useDialogo();
  const [g, setG] = useState<EstadoGoogle | null>(null);
  const [ocupado, setOcupado] = useState(false);

  async function mirar() {
    const r = await fetch("/api/google/estado", { credentials: "same-origin" });
    if (r.ok) setG((await r.json()) as EstadoGoogle);
  }

  useEffect(() => {
    void mirar();
  }, []);

  async function conectar() {
    setOcupado(true);
    try {
      const r = await fetch("/api/google/conectar", { credentials: "same-origin" });
      const cuerpo = (await r.json()) as { url?: string; error?: string };
      // Se va a Google en esta misma pestaña: al volver, la página de vuelta
      // trae un enlace al panel. Abrir otra dejaría dos BILBOBOT abiertos.
      if (cuerpo.url) window.location.href = cuerpo.url;
      else await avisar(cuerpo.error ?? "No se pudo empezar la conexión.");
    } finally {
      setOcupado(false);
    }
  }

  async function desconectar() {
    if (!(await preguntar("¿Desconectar la cuenta de Google?", "Desconectar", true))) return;
    setOcupado(true);
    try {
      const r = await fetch("/api/google", { method: "DELETE", credentials: "same-origin" });
      const cuerpo = (await r.json()) as { nota?: string };
      if (cuerpo.nota) await avisar(cuerpo.nota);
      await mirar();
    } finally {
      setOcupado(false);
    }
  }

  if (!g) return null;

  return (
    <section className="bloque">
      {dialogo}
      <h2>Google</h2>

      {!g.configurado ? (
        <p className="nota">{g.falta}</p>
      ) : g.conectado ? (
        <>
          <p className="nota">
            Conectado como tú, en <strong>sólo lectura</strong>. Ve{" "}
            <strong>{g.searchConsole} propiedades</strong> en Search Console y{" "}
            <strong>{g.analytics}</strong> en Analytics. Las webs nuevas aparecen solas.
          </p>
          {/*
            «Volver a autorizar», estando conectado.
            ── Por qué hacía falta (13/10/2026) ───────────────────────────────
            Un testigo ya emitido NO gana permisos nuevos: al añadir el de Tag
            Manager había que volver a pasar por el consentimiento, y aquí sólo
            estaba «Desconectar». La única forma era desconectar y conectar, con
            un hueco por medio en el que cualquier vuelta que pidiera a Google
            fallaba.
            No pierde nada: la autorización va con `include_granted_scopes`, así
            que los permisos de antes siguen.
          */}
          <button className="acc" onClick={() => void conectar()} disabled={ocupado}>
            {ocupado ? "Abriendo Google…" : "Volver a autorizar"}
          </button>{" "}
          <button className="acc peligro" onClick={() => void desconectar()} disabled={ocupado}>
            Desconectar
          </button>
          <p className="s">
            «Volver a autorizar» hace falta cuando se añade un permiso nuevo: el que ya
            está guardado no lo gana solo. No se pierde ninguno de los de antes.
          </p>
        </>
      ) : (
        <>
          <p className="nota">
            Sin conectar. Al autorizar verás un aviso de «Google no ha verificado esta
            aplicación»: es normal en una herramienta propia — entra en «Configuración
            avanzada» y sigue. Sólo se piden permisos de lectura.
          </p>
          <button className="acc" onClick={() => void conectar()} disabled={ocupado}>
            {ocupado ? "Abriendo Google…" : "Conectar mi cuenta de Google"}
          </button>
        </>
      )}
    </section>
  );
}

export function Control() {
  const { preguntar, pedir, avisar, dialogo } = useDialogo();
  const [datos, setDatos] = useState<Control | null>(null);
  // Lo que añade la Fase 4 a cada máquina, por su id: viene de /api/workers.
  const [paneles, setPaneles] = useState<Record<string, Panel>>({});
  // Si la API está permitida para alguna capacidad (Fase 5). Hoy, apagada para todas.
  const [api, setApi] = useState<ApiDeAgentes | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [cara, setCara] = useState<Cara>("sistema");
  /** Dentro de Incidencias: los fallos de hoy o los ataques de siempre. */
  const [sub, setSub] = useState<"fallos" | "hackeos">("fallos");
  const [vuelta, setVuelta] = useState<Vuelta | null>(null);
  /**
   * Con 91 webs, la lista entera es para mirar de vez en cuando; lo que se
   * quiere ver casi siempre son las que fallan. Por eso empieza filtrada.
   */
  const [soloConAlgo, setSoloConAlgo] = useState(true);

  /**
   * La vigilancia se pide aparte y sólo al abrir su pestaña.
   *
   * Es lo que vio en su última vuelta, no una comprobación nueva: pedir quince
   * webs cada vez que alguien abre el panel las castigaría sin motivo, y con
   * los cortafuegos de por medio acabaría baneándonos — ya pasó.
   */
  useEffect(() => {
    if (cara !== "vigilancia") return;
    void (async () => {
      const r = await fetch("/api/control/vigilancia", { credentials: "same-origin" });
      if (r.ok) setVuelta(((await r.json()) as { vuelta: Vuelta | null }).vuelta);
    })();
  }, [cara]);

  async function cargar() {
    /*
     * Las dos a la vez. `/api/control` trae la foto del sistema y el trabajo en
     * marcha; `/api/workers` trae lo que la Fase 4 añade a cada máquina —la
     * cola, lo de hoy, sus herramientas—. Si la segunda falla, la pantalla se
     * pinta igual sin esas líneas: no puede quedarse en blanco por un extra.
     */
    const [r, w] = await Promise.all([
      fetch("/api/control", { credentials: "same-origin" }),
      fetch("/api/workers", { credentials: "same-origin" }).catch(() => null),
    ]);
    if (r.ok) setDatos((await r.json()) as Control);
    if (w?.ok) {
      const { workers, api: laApi } = (await w.json()) as {
        workers: { id: string; panel?: Panel }[];
        api?: ApiDeAgentes;
      };
      setPaneles(
        Object.fromEntries(workers.flatMap((x) => (x.panel ? [[x.id, x.panel] as const] : []))),
      );
      setApi(laApi ?? null);
    }
  }

  useEffect(() => {
    void cargar();
    // Cada cinco segundos: es una pantalla que se deja abierta mientras algo
    // corre, y un progreso que no avanza en pantalla no sirve de nada.
    const t = setInterval(() => void cargar(), 5_000);
    return () => clearInterval(t);
  }, []);

  async function freno(parar: boolean) {
    const motivo = parar
      ? await pedir("¿Por qué paras el reparto? Queda registrado.", "parada manual")
      : "reanudado desde el panel de control";
    if (parar && motivo === null) return;

    setOcupado(true);
    await fetch("/api/hoy/freno", {
      method: "POST",
      credentials: "same-origin",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ parar, motivo }),
    });
    await cargar();
    setOcupado(false);
  }

  /**
   * Apagar una regla de la casa.
   *
   * Se pregunta antes porque deja de aplicarse en el turno siguiente y lo que se
   * hizo con ella puesta no se deshace solo. Y se pide el motivo: dentro de dos
   * meses, «se apagó» sin porqué no explica nada.
   */
  async function apagarRegla(id: string) {
    const motivo = await pedir("¿Por qué la apagas? Queda escrito.", "ya no hace falta");
    if (motivo === null) return;

    setOcupado(true);
    await fetch(`/api/control/reglas/${id}/apagar`, {
      method: "POST",
      credentials: "same-origin",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ motivo }),
    });
    await cargar();
    setOcupado(false);
  }

  /**
   * Cambiar el cerebro.
   *
   * Se recarga entero después en vez de tocar el estado a mano: el servidor
   * recalcula los avisos, y una pantalla que se cree su propia versión de lo
   * guardado acaba enseñando algo que no es lo que hay.
   */
  async function cambiarCerebro(cambio: Record<string, unknown>) {
    setOcupado(true);
    const r = await fetch("/api/control/cerebro", {
      method: "PUT",
      credentials: "same-origin",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(cambio),
    });
    if (!r.ok) {
      await avisar(((await r.json()) as { error?: string }).error ?? "No se pudo cambiar.");
    }
    await cargar();
    setOcupado(false);
  }

  async function mirarAhora() {
    setOcupado(true);
    const r = await fetch("/api/control/vigilancia", {
      method: "POST",
      credentials: "same-origin",
    });
    if (r.ok) setVuelta(((await r.json()) as { vuelta: Vuelta }).vuelta);
    setOcupado(false);
  }

  /**
   * Da de baja un ordenador.
   *
   * Se pregunta antes porque desde aquí no hay deshacer: para volver a usarlo
   * hay que darlo de alta otra vez con un código nuevo.
   */
  async function darDeBaja(id: string, nombre: string) {
    const seguro = await preguntar(
      `¿Dar de baja «${nombre}»?\n\nDejará de poder conectarse en el acto. ` +
        "Para volver a usarlo habría que darlo de alta de nuevo con un código.",
      "Dar de baja",
      true,
    );
    if (!seguro) return;
    setOcupado(true);
    await fetch(`/api/workers/${id}`, { method: "DELETE", credentials: "same-origin" });
    await cargar();
    setOcupado(false);
  }

  async function cancelar(t: Trabajo) {
    if (
      !(await preguntar(
        `¿Cancelar «${t.titulo}»?\n\nSi está corriendo, se para el proceso en la máquina.`,
        "Cancelar el trabajo",
        true,
      ))
    ) {
      return;
    }
    setOcupado(true);
    await fetch(`/api/hoy/cancelar/${t.jobId}`, { method: "POST", credentials: "same-origin" });
    await cargar();
    setOcupado(false);
  }

  if (!datos) return <div className="provisional">Cargando…</div>;

  const { sistema, ordenadores, errores } = datos;
  /* Sólo los del proveedor activo: ofrecer los del otro sería ofrecer una
     combinación que no funciona, porque se llama a uno a la vez. */
  const modelosDelProveedor = sistema.cerebro.catalogo.filter(
    (c) => c.proveedor === sistema.cerebro.proveedor,
  );
  /* Las apagadas van aparte: ni en el recuento ni entre las que trabajan. */
  const apagadas = ordenadores.filter((o) => o.estado === "apagado");
  const reglas = datos.reglas ?? [];
  const enPie = ordenadores.filter((o) => o.estado !== "apagado");
  const enLinea = enPie.filter((o) => o.estado !== "sin-senal").length;

  return (
    <div className="control pnl-control">
      {dialogo}
      <header className="portada">
        <div className="que-es">
          <h1>Panel de control</h1>
          <p>Las máquinas, lo que ejecutan y si el sistema está sano.</p>
        </div>

        <div className="mandos">
          <TamanoDeLetra />
          {sistema.reparto.pausado ? (
            <button className="acc" onClick={() => void freno(false)} disabled={ocupado}>
              Reanudar el reparto
            </button>
          ) : (
            <button className="acc peligro" onClick={() => void freno(true)} disabled={ocupado}>
              Parar el reparto
            </button>
          )}
        </div>
      </header>

      {/* Las cuatro caras. Van arriba del todo porque son la navegación. */}
      <nav className="caras">
        {CARAS.map((c) => (
          <button
            key={c.id}
            className={cara === c.id ? "cara activa" : "cara"}
            onClick={() => setCara(c.id)}
          >
            {c.titulo}
            {/* Los avisos se ven desde fuera de su pestaña, o no servirían. */}
            {c.id === "fallos" && errores.length > 0 && (
              <span className="pill mal">{errores.length}</span>
            )}
            {c.id === "cerebro" && sistema.cerebro.pegas.length > 0 && (
              <span className="pill aviso">{sistema.cerebro.pegas.length}</span>
            )}
          </button>
        ))}
      </nav>

      {/* Lo primero: si el reparto está parado, todo lo demás da igual. */}
      {sistema.reparto.pausado && (
        <div className="barra-freno">
          <strong>Reparto parado.</strong>
          {sistema.reparto.motivo ? ` ${sistema.reparto.motivo}.` : ""} No sale trabajo nuevo hacia
          las máquinas; lo que ya corría sigue.
        </div>
      )}

      {sistema.reparto.vetadas.length > 0 && (
        <div className="barra-veto">
          <strong>Capacidades vetadas:</strong> {sistema.reparto.vetadas.join(", ")}.
          {sistema.reparto.motivoDelVeto ? ` ${sistema.reparto.motivoDelVeto}` : ""}
        </div>
      )}

      {cara === "sistema" && (
        <>
      {/* ── El estado del sistema ── */}
      <div className="cifras">
        <div className={sistema.baseDeDatos.latenciaMs > 500 ? "cifra alerta" : "cifra"}>
          <span className="n">{sistema.baseDeDatos.latenciaMs} ms</span>
          <span className="q">PostgreSQL</span>
        </div>
        <div className="cifra">
          <span className="n">{duracion(sistema.director.arrancadoHaceSegundos)}</span>
          <span className="q">Director en pie</span>
        </div>
        <div className="cifra">
          <span className="n cerebro">{sistema.cerebro.modeloDirector}</span>
          <span className="q">piensa con esto</span>
        </div>
        <div className="cifra">
          <span className="n">
            {enLinea}/{enPie.length}
          </span>
          <span className="q">ordenadores</span>
        </div>
        <div className={sistema.colas.esperando > 0 ? "cifra alerta" : "cifra"}>
          <span className="n">{sistema.colas.esperando}</span>
          <span className="q">en cola</span>
        </div>
        <div className="cifra">
          <span className="n">{sistema.colas.enMarcha}</span>
          <span className="q">ejecutándose</span>
        </div>
        <div className={sistema.colas.fallidosHoy > 0 ? "cifra alerta" : "cifra"}>
          <span className="n">{sistema.colas.fallidosHoy}</span>
          <span className="q">fallidos hoy</span>
        </div>
        <div className="cifra">
          <span className="n">{sistema.gasto.mesEur.toFixed(2)} €</span>
          <span className="q">de {sistema.gasto.presupuestoEur} € este mes</span>
        </div>
      </div>

      {/* La API para los agentes (Fase 5): si no puede ninguno, se espera; no se paga aparte. */}
      {api && (
        <p className="api-de-agentes">
          {api.permitidas.length === 0
            ? "API para los agentes: apagada para todas las capacidades. Si ningún agente puede, el trabajo espera; nunca se pasa a pagar aparte."
            : `API para los agentes: permitida para ${api.permitidas
                .map((p) => `${p.capacidad} (hasta ${p.topeEurosDia} €/día)`)
                .join(", ")}${api.ejecutor ? "" : ", pero todavía no hay con qué ejecutarla: no se usa"}.`}
        </p>
      )}

      {/* ── Las máquinas ── */}
      <div className="maquinas">
        {enPie.map((o) => (
          <section className={`maquina ${ESTADOS[o.estado].clase}`} key={o.id}>
            <header>
              <span className="orbe" aria-hidden="true" />
              <div className="quien">
                <div className="t">{o.nombre}</div>
                <div className="s">
                  {[o.hostname, o.plataforma, o.version ? `agente ${o.version}` : null]
                    .filter(Boolean)
                    .join(" · ")}
                  {/*
                    * Si el agente va al día. Un ordenador con código viejo conecta y
                    * late igual que uno al día: sin esto no se nota, y desde el
                    * 26/09/2026 uno viejo se come los campos nuevos de los encargos.
                    */}
                  {o.alDia === false ? (
                    <span className="agente-viejo" title={o.loDeLaVersion}>
                      {" · agente por detrás"}
                    </span>
                  ) : o.alDia === null ? (
                    <span className="agente-viejo" title={o.loDeLaVersion}>
                      {" · versión sin comprobar"}
                    </span>
                  ) : null}
                </div>
              </div>
              <span className={`estado ${ESTADOS[o.estado].clase}`}>
                {ESTADOS[o.estado].texto}
              </span>
            </header>

            <dl className="detalle">
              <div>
                <dt>Último latido</dt>
                <dd>
                  {o.segundosDesdeLatido === null
                    ? "nunca"
                    : o.segundosDesdeLatido < 90
                      ? `hace ${o.segundosDesdeLatido} s`
                      : `hace ${duracion(o.segundosDesdeLatido)}`}
                </dd>
              </div>
              <div>
                <dt>Procesador</dt>
                <dd>
                  {o.cpu ?? "—"}
                  {o.nucleos ? ` · ${o.nucleos} núcleos` : ""}
                  {/* La carga se mide entre dos latidos, así que el primero de
                      una máquina recién conectada no la trae todavía. */}
                  {o.carga !== null ? ` · al ${o.carga} %` : ""}
                </dd>
              </div>
              <div>
                <dt>Memoria</dt>
                <dd>
                  {o.memoriaMb ? `${Math.round(o.memoriaMb / 1024)} GB` : "—"}
                  {/* Libre sólo si la máquina está dando señal. Un número de
                      hace tres días, pintado sin fecha, se lee como de ahora. */}
                  {o.memoriaLibreMb !== null && o.memoriaMb
                    ? ` · ${Math.round(o.memoriaLibreMb / 1024)} GB libres`
                    : ""}
                </dd>
              </div>
              <div>
                <dt>Disco</dt>
                <dd>
                  {o.discoLibreGb === null ? (
                    "—"
                  ) : (
                    <span className={o.discoLibreGb < 2 ? "poco-disco" : undefined}>
                      {o.discoLibreGb} GB libres
                      {o.discoTotalGb ? ` de ${o.discoTotalGb}` : ""}
                      {o.discoLibreGb < 2 ? " · se va a quedar sin sitio" : ""}
                    </span>
                  )}
                </dd>
              </div>
              <div>
                <dt>Trabajos hechos</dt>
                <dd>{o.completados ?? 0}</dd>
              </div>
              <div>
                <dt>A la vez</dt>
                <dd>
                  {o.trabajando.length} de {o.aLaVez ?? 1}
                </dd>
              </div>
            </dl>

            <div className="capacidades">
              {o.capacidades.map((c) => (
                <span className="pill" key={c}>
                  {c}
                </span>
              ))}
            </div>

            <ExtrasDeMaquina panel={paneles[o.id]} />

            {o.trabajando.length === 0 ? (
              <p className="vacio2">
                {o.estado === "sin-senal"
                  ? "No responde. Si es un portátil, puede estar apagado."
                  : "Sin trabajo ahora mismo."}
              </p>
            ) : (
              o.trabajando.map((t) => (
                <div className="tarea" key={t.jobId}>
                  <div className="txt">
                    <div className="t">{t.titulo}</div>
                    <div className="s">
                      {[
                        t.capacidad,
                        t.agente,
                        duracion(t.segundos),
                        t.intentos > 1 ? `intento ${t.intentos}` : null,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </div>

                    {t.paso && (
                      <div className="paso">
                        {t.deCuantos ? `${t.hechos ?? 0} de ${t.deCuantos} · ` : ""}
                        {t.paso}
                      </div>
                    )}

                    {t.porcentaje !== null && (
                      <div className="progreso">
                        <span style={{ width: `${t.porcentaje}%` }} />
                      </div>
                    )}

                    {/* Un trabajo largo y uno colgado se ven igual en un
                        cronómetro. Lo que los distingue es cuándo avanzó. */}
                    {t.sinAvanzarSegundos !== null && t.sinAvanzarSegundos > 180 && (
                      <div className="s atascado">
                        Sin avanzar desde hace {duracion(t.sinAvanzarSegundos)}
                      </div>
                    )}
                  </div>

                  <button className="acc sec" onClick={() => void cancelar(t)} disabled={ocupado}>
                    Cancelar
                  </button>
                </div>
              ))
            )}

            <Historial workerId={o.id} />

            {/* ── Dar de baja ──
              *
              * Corta el acceso en el acto: su token deja de valer aunque el
              * agente siga corriendo en ese ordenador. La ficha se conserva
              * —con cuántos trabajos hizo y cuántos fallaron—, porque esa
              * historia es justo lo que se quiere mirar después.
              */}
            <div className="pie-maquina">
              <button
                className="acc peligro"
                disabled={ocupado}
                onClick={() => void darDeBaja(o.id, o.nombre)}
              >
                Dar de baja este ordenador
              </button>
            </div>
          </section>
        ))}
      </div>

      {/*
        * Las apagadas, aparte y en corto.
        *
        * Siguen a la vista —dadas de alta están, y hay que poder darlas de baja—
        * pero no entre las que trabajan: una lista donde tres de cinco llevan
        * semanas apagadas enseña a no mirar la lista.
        */}
      {/*
        * ── Las reglas de la casa (Bloque 5) ────────────────────────────
        *
        * Enteras y de un vistazo, que es lo que pide el pre-vuelo: si la lista no
        * cabe en una pantalla, ya es demasiada. Y con su botón de apagar, porque
        * la tarjeta que las aprueba promete que se apagan desde aquí —y una
        * promesa sin botón es una promesa—.
        */}
      {reglas.length > 0 && (
        <div className="reglas-casa">
          <h3>Reglas de la casa</h3>
          <p className="s">
            Instrucciones que BilboBot lee en cada turno que les toca. No le dan ningún permiso
            nuevo: lo que necesita tu sí lo sigue necesitando.
          </p>
          <ul>
            {reglas.map((r) => (
              <li key={r.id} className={r.estado === "activa" ? "" : "apagada"}>
                <div className="t">
                  <strong>{r.titulo}</strong>
                  <span className="donde">{r.ambito === "siempre" ? "en todos los turnos" : `grupo ${r.ambito}`}</span>
                  {r.estado !== "activa" && <span className="donde">apagada</span>}
                </div>
                <div className="s">
                  Cuándo: {r.cuando} — Entonces: {r.entonces}
                </div>
                {r.estado === "apagada" && r.apagadaPorQue && (
                  <div className="s">Se apagó: {r.apagadaPorQue}</div>
                )}
                {r.estado === "activa" && (
                  <button type="button" onClick={() => apagarRegla(r.id)}>
                    Apagar
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {apagadas.length > 0 && (
        <div className="apagadas">
          <h3>
            {apagadas.length === 1 ? "Un ordenador apagado" : `${apagadas.length} ordenadores apagados`}
          </h3>
          <ul>
            {apagadas.map((o) => (
              <li key={o.id}>
                <strong>{o.nombre}</strong>
                <span>{desdeCuandoApagada(o)}</span>
              </li>
            ))}
          </ul>
          <p className="s">
            No cuentan como ordenadores disponibles. En cuanto uno arranque, su lanzador se pone al
            día solo y vuelve arriba.
          </p>
        </div>
      )}

          <GooglePanel />

          <section className="bloque">
            <h2>Avisos al móvil</h2>
            <AvisarmeEnEsteMovil />
          </section>

          {/* ── Dar de alta un ordenador ──
            *
            * Solo el boton: las maquinas ya se ven arriba, en «Las maquinas»,
            * con su ultimo latido y lo que saben hacer. Poner aqui otra lista
            * seria enseñar lo mismo dos veces con dos formatos distintos, y asi
            * es como se dejan de mirar las dos.
            */}
          <AnadirOrdenador />

        </>
      )}

      {cara === "fallos" && (
        <>
      {/* ── Dos cosas distintas, dos pestañas ──
        *
        * Un trabajo que no salio y un ataque no se miran igual ni en el mismo
        * momento: los fallos se revisan hoy, los ataques se releen dentro de
        * seis meses. Juntarlos en una lista hacia que lo segundo quedara
        * enterrado bajo lo primero.
        */}
      <div className="sub-caras">
        {[
          { id: "fallos" as const, titulo: "Últimos fallos" },
          { id: "hackeos" as const, titulo: "Hackeos" },
        ].map((s) => (
          <button
            key={s.id}
            type="button"
            className={sub === s.id ? "activa" : ""}
            onClick={() => setSub(s.id)}
          >
            {s.titulo}
            {s.id === "fallos" && errores.length > 0 && (
              <span className="pill mal">{errores.length}</span>
            )}
          </button>
        ))}
      </div>

      {sub === "fallos" && (
        <>
      {/* ── Los últimos fallos ── */}
      <section className="caja">
        <header>
          <h2>
            Últimos fallos
            {errores.length > 0 && <span className="pill mal">{errores.length}</span>}
          </h2>
        </header>

        {errores.length === 0 ? (
          <p className="vacio2">Ningún trabajo ha fallado.</p>
        ) : (
          errores.map((e) => (
            <div className="fallo" key={e.jobId}>
              <div className="t">
                {e.capacidad}
                {e.worker && <span className="pill">{e.worker}</span>}
                {e.intentos > 1 && <span className="pill">intento {e.intentos}</span>}
              </div>
              <div className="s">{e.error ?? "sin motivo registrado"}</div>
            </div>
          ))
        )}
      </section>

      {/* ── Lo que este panel NO dice ──
       *
       * Va con los fallos: las dos cosas responden a «¿por qué no veo esto?».
       */}
      <section className="caja no-se-mide">
        <header>
          <h2>Lo que este panel NO dice</h2>
        </header>
        {sistema.loQueNoSeMide.map((x) => (
          <div className="fallo" key={x.que}>
            <div className="t">{x.que}</div>
            <div className="s">{x.porque}</div>
          </div>
        ))}
      </section>
        </>
      )}

      {sub === "hackeos" && (
        <>
      {/* ── Lo que pasó ── */}
      <section className="caja">
        <header>
          <h2>Lo que pasó</h2>
        </header>
        {HACKEOS.map((h) => (
          <div className="hackeo" key={h.fecha}>
            <div className="t">
              <span className="pill mal">{h.fecha}</span> {h.titulo}
            </div>
            <div className="s">{h.resumen}</div>
            <ul className="docs">
              {h.documentos.map((d) => (
                <li key={d.url}>
                  <a href={d.url} target="_blank" rel="noreferrer">
                    {d.nombre}
                  </a>
                  <span className="s"> — {d.que}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </section>

      {/* ── Y lo que se ha hecho ── */}
      <section className="caja">
        <header>
          <h2>Lo que se ha hecho</h2>
        </header>
        <p className="vacio2">
          Día a día, desde la noche del ataque. El detalle y el porqué de cada decisión
          están en{" "}
          <a
            href="https://claude.ai/code/artifact/5833c27a-0939-4510-9a39-1817cd567903"
            target="_blank"
            rel="noreferrer"
          >
            El parte del ataque
          </a>
          .
        </p>
        {LO_HECHO.map((d) => (
          <div className="jornada" key={d.dia}>
            <div className="t">
              <span className="pill">{d.dia}</span> {d.titulo}
            </div>
            <ul>
              {d.puntos.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          </div>
        ))}
      </section>
        </>
      )}
        </>
      )}

      {cara === "vigilancia" && (
        <>
          {!vuelta ? (
            <section className="caja">
              <p className="vacio2">
                Todavía no ha dado ninguna vuelta. La primera es a los 30 segundos de arrancar, y
                luego cada diez minutos.
              </p>
            </section>
          ) : (
            <>
              {/**
                * La hora de la última vuelta, arriba del todo y grande.
                *
                * No es un adorno: un vigía que se muere no se pone en rojo, se
                * queda en verde para siempre porque nadie vuelve a
                * preguntarle. Un «hace 9 horas» en algo que va cada diez
                * minutos canta solo — y eso es exactamente lo que faltó el
                * 15/08, cuando MariaDB estuvo caída una noche entera.
                */}
              <header className="portada vigilancia-portada">
                <div className="que-es">
                  <h1>{hace(vuelta.cuando)}</h1>
                  <p>
                    última vuelta · {vuelta.webs.length} webs · tardó{" "}
                    {(vuelta.duracionMs / 1000).toFixed(1)} s
                  </p>
                </div>
                <div className="mandos">
                  <button className="acc" onClick={() => void mirarAhora()} disabled={ocupado}>
                    Mirar ahora
                  </button>
                </div>
              </header>

              {vuelta.problemas.length === 0 ? (
                <div className="todo-bien">Nada que mirar: webs, certificados y servidor en orden.</div>
              ) : (
                <section className="caja">
                  <header>
                    <h2>
                      Lo que hay que mirar
                      <span className="pill mal">{vuelta.problemas.length}</span>
                    </h2>
                  </header>
                  {vuelta.problemas.map((p) => (
                    <div className="fallo" key={p.huella}>
                      <div className="t">
                        {/*
                         * Emoji Y palabra, no uno de los dos.
                         *
                         * Aquí había sólo el círculo, y rojo contra ámbar a este
                         * tamaño son dos puntos grises para quien no distingue
                         * esos dos colores — nada más en la fila lo dice. Con la
                         * palabra además se puede buscar en la página.
                         */}
                        <span className={`pill gravedad ${p.gravedad}`}>
                          {p.gravedad === "urgente"
                            ? "🔴"
                            : p.gravedad === "tranquilo"
                              ? "🟢"
                              : "🟠"}{" "}
                          {p.gravedad}
                        </span>{" "}
                        {p.que}
                      </div>
                      <div className="s">{p.detalle}</div>
                    </div>
                  ))}
                </section>
              )}

              {/* ── El servidor ── */}
              <section className="caja">
                <header>
                  <h2>Servidor</h2>
                </header>
                <dl className="siempre">
                  <div>
                    <dt>Memoria disponible</dt>
                    <dd>
                      {(vuelta.servidor.memoria.disponibleMb / 1024).toFixed(1)} GB de{" "}
                      {(vuelta.servidor.memoria.totalMb / 1024).toFixed(1)} ·{" "}
                      {vuelta.servidor.memoria.porcentajeDisponible} %
                      {/* Las tres cifras juntas para que el número pequeño no
                          asuste: «libre» sin la caché no significa nada. */}
                      <br />
                      <span className="tenue">
                        libre {(vuelta.servidor.memoria.libreMb / 1024).toFixed(1)} · caché{" "}
                        {(vuelta.servidor.memoria.cacheMb / 1024).toFixed(1)} GB
                      </span>
                    </dd>
                  </div>
                  <div>
                    <dt>Swap</dt>
                    <dd>
                      {(vuelta.servidor.memoria.swapLibreMb / 1024).toFixed(1)} GB libres de{" "}
                      {(vuelta.servidor.memoria.swapTotalMb / 1024).toFixed(1)}
                    </dd>
                  </div>
                  <div>
                    <dt>Disco</dt>
                    <dd>
                      {vuelta.servidor.disco.libreGb} GB libres · {vuelta.servidor.disco.usadoPorCiento} %
                      usado
                    </dd>
                  </div>
                  <div>
                    <dt>Carga</dt>
                    <dd>
                      {vuelta.servidor.carga.uno.toFixed(2)} · {vuelta.servidor.carga.cinco.toFixed(2)}{" "}
                      · {vuelta.servidor.carga.quince.toFixed(2)}
                      <br />
                      {/* Sin los núcleos, «3,53» no dice nada. */}
                      <span className="tenue">en {vuelta.servidor.carga.nucleos} núcleos</span>
                    </dd>
                  </div>
                  <div>
                    <dt>En pie</dt>
                    <dd>
                      {duracion(vuelta.servidor.enPieSegundos)}
                      {/* Llevar mucho tiempo en pie no es una medalla: es tiempo
                          sin comprobar que la máquina arranca. El 27/08/2026
                          eran 18 semanas y no arrancó. */}
                      {vuelta.servidor.enPieSegundos > 60 * 60 * 24 * 60 && (
                        <>
                          <br />
                          <span className="tenue">
                            sin reiniciar desde hace más de dos meses
                          </span>
                        </>
                      )}
                    </dd>
                  </div>
                  {vuelta.servidor.arranque && (
                    <div>
                      <dt>¿Sobreviviría a un reinicio?</dt>
                      <dd>
                        {vuelta.servidor.arranque.proximoReinicioSinProbar ? (
                          <>
                            <strong className="aviso-texto">No lo sabemos</strong>
                            <br />
                            <span className="tenue">
                              arrancaría con {vuelta.servidor.arranque.kernelProximoArranque},
                              que nunca ha arrancado aquí
                            </span>
                          </>
                        ) : (
                          <>
                            Sí
                            <br />
                            <span className="tenue">
                              arrancaría con {vuelta.servidor.arranque.kernelActual}, el mismo
                              que está corriendo
                            </span>
                          </>
                        )}
                        {vuelta.servidor.arranque.kernelMasNuevoInstalado &&
                          vuelta.servidor.arranque.kernelMasNuevoInstalado !==
                            vuelta.servidor.arranque.kernelActual && (
                            <>
                              <br />
                              <span className="tenue">
                                hay uno más nuevo instalado sin probar:{" "}
                                {vuelta.servidor.arranque.kernelMasNuevoInstalado}
                              </span>
                            </>
                          )}
                        {!vuelta.servidor.arranque.registroPersistente && (
                          <>
                            <br />
                            <span className="tenue">
                              y el arranque no deja rastro en disco
                            </span>
                          </>
                        )}
                      </dd>
                    </div>
                  )}
                  <div>
                    <dt>Lo que más ocupa</dt>
                    <dd>
                      {vuelta.servidor.procesos.slice(0, 3).map((p) => (
                        <span key={p.nombre}>
                          {p.nombre} {(p.mb / 1024).toFixed(1)} GB
                          <br />
                        </span>
                      ))}
                    </dd>
                  </div>
                </dl>
              </section>

              {/* ── Las webs ── */}
              <section className="caja">
                <header>
                  <h2>
                    Webs alojadas
                    <span className="pill">{vuelta.webs.length}</span>
                  </h2>
                  <p className="ayuda">
                    Todo lo que hay en el servidor, más los clientes alojados fuera. Las nuevas
                    aparecen solas.{" "}
                    {vuelta.webs.filter((w) => !w.conCliente).length > 0 && (
                      <>
                        <strong>{vuelta.webs.filter((w) => !w.conCliente).length}</strong> no están
                        atribuidas a ningún cliente de la cartera.
                      </>
                    )}
                  </p>

                  {/* El filtro va arriba: con 91 filas, ver la lista entera es
                      lo excepcional y ver las que fallan es lo normal. */}
                  <div className="filtro-webs">
                    <button
                      className={soloConAlgo ? "chip elegido" : "chip"}
                      onClick={() => setSoloConAlgo(true)}
                    >
                      Sólo las que fallan
                      <span className="n">
                        {vuelta.webs.filter((w) => w.estado !== "bien").length}
                      </span>
                    </button>
                    <button
                      className={soloConAlgo ? "chip" : "chip elegido"}
                      onClick={() => setSoloConAlgo(false)}
                    >
                      Todas
                      <span className="n">{vuelta.webs.length}</span>
                    </button>
                  </div>
                </header>

                {/**
                  * Filas, no tabla.
                  *
                  * Una tabla con el detalle dentro de la celda se hacía más
                  * ancha que la pantalla, y al desplazarla se perdía de vista
                  * justo la primera columna — el nombre del cliente, que es lo
                  * único que identifica la fila. Así cada web es un bloque que
                  * se encoge, y el detalle largo vive en el tooltip.
                  */}
                <div className="lista-webs">
                  {vuelta.webs
                    .filter((w) => !soloConAlgo || w.estado !== "bien")
                    .map((w) => (
                      <div className="web" key={w.dominio}>
                        <div className="quien">
                          {/* `title` lleva el nombre entero: los nombres
                              fiscales no caben y cortarlos sin más dejaría
                              «...DE VIZCAYA S.A.» sin saber de quién es. */}
                          <span
                            className={w.conCliente ? "nom" : "nom sin-cliente"}
                            title={w.conCliente ? w.cliente : "Sin cliente en la cartera"}
                          >
                            {w.cliente}
                          </span>
                          <span className="dom" title={w.dominio}>
                            {w.dominio.replace(/^https?:\/\//, "").replace(/\/$/, "")}
                            {w.esRedireccion && " · redirección"}
                          </span>
                        </div>

                        <div className="cifras-web">
                          {/* El detalle entero en el tooltip: en la fila sólo
                              cabe la palabra, y la explicación estorbaría en
                              las 78 que están bien. */}
                          <span
                            className={`estado-web ${w.estado === "bien" ? "ok" : w.estado === "no-comprobable" || w.estado === "lenta" ? "flojo" : "mal"}`}
                            title={w.detalle}
                          >
                            {w.estado === "bien"
                              ? "bien"
                              : w.estado === "no-comprobable"
                                ? "no se puede ver"
                                : w.estado === "no-responde"
                                  ? "no responde"
                                  : w.estado === "base-muerta"
                                    ? "sin base de datos"
                                    : w.estado === "sin-configurar"
                                      ? "sin sitio detrás"
                                      : w.estado === "lenta"
                                        ? "lenta"
                                        : "error"}
                          </span>

                          <span className="tarda" title="Lo que tarda en responder">
                            {(w.ms / 1000).toFixed(1)} s
                          </span>

                          <span
                            className={
                              w.diasDeCertificado === null
                                ? "cert tenue"
                                : w.diasDeCertificado <= 21
                                  ? "cert flojo"
                                  : "cert ok"
                            }
                            title={
                              w.certificadoCaduca
                                ? `El certificado caduca el ${w.certificadoCaduca}`
                                : "No se ha podido leer el certificado"
                            }
                          >
                            {w.diasDeCertificado === null ? "—" : `${w.diasDeCertificado} d`}
                          </span>
                        </div>
                      </div>
                    ))}

                  {soloConAlgo && vuelta.webs.every((w) => w.estado === "bien") && (
                    <p className="vacio2">Las {vuelta.webs.length} responden con normalidad.</p>
                  )}
                </div>
              </section>

              {/* ── El blindaje, web por web ──
                *
                * La unidad es la WEB, no el problema. Con una lista plana, una
                * web a la que le faltan tres cosas y veinte a las que les falta
                * una son cuarenta filas seguidas sin jerarquía — el mismo fallo
                * que «668 plugins sin actualizar en 63 webs», que nadie abre.
                *
                * Y sobre todo: aquí se contesta «¿cómo está ESTA web?», que es
                * lo que uno se pregunta cuando llama un cliente.
                */}
              {vuelta.blindaje && (
                <section className="caja">
                  <header>
                    <h2>
                      Blindaje
                      {vuelta.blindaje.resumen.total === 0 ? (
                        <span className="pill mal">sin informe</span>
                      ) : vuelta.blindaje.resumen.conFaltas > 0 ? (
                        <span className="pill mal">
                          {vuelta.blindaje.resumen.conFaltas} con faltas
                        </span>
                      ) : (
                        <span className="pill ok">
                          {vuelta.blindaje.resumen.enteras} enteras
                        </span>
                      )}
                    </h2>
                    {/*
                      * Cuando se miro, y no cuando se pinta.
                      *
                      * Quien mira las webs es un programa que corre como root en
                      * el servidor —BilboBot salio de root el 29/08 y ya no
                      * puede entrar en las carpetas de los clientes—, y pasa en
                      * su propio horario. Decir la hora aqui es lo que separa
                      * «esto es de ahora» de «esto era de ayer».
                      */}
                    {vuelta.blindaje.cuando && (
                      <p className="s">Mirado por el inspector: {hace(vuelta.blindaje.cuando)}</p>
                    )}
                  </header>

                  {/*
                    * Las tres cifras separadas, y «sin mirar» aparte.
                    *
                    * No se suma a ninguna de las otras dos: meterla con las
                    * buenas sería mentir, y con las malas, alarmar de balde.
                    */}
                  <dl className="siempre">
                    <div>
                      <dt>Enteras</dt>
                      <dd className="cifra">{vuelta.blindaje.resumen.enteras}</dd>
                    </div>
                    <div>
                      <dt>Les falta algo</dt>
                      <dd className="cifra">{vuelta.blindaje.resumen.conFaltas}</dd>
                    </div>
                    <div>
                      <dt>No se han podido mirar</dt>
                      <dd className="cifra">{vuelta.blindaje.resumen.sinMirar}</dd>
                    </div>
                  </dl>

                  {vuelta.blindaje.repuesto.length > 0 && (
                    <div className="nota">
                      El inspector repuso solo {vuelta.blindaje.repuesto.length} cosa(s) en su
                      ultima ronda.
                    </div>
                  )}

                  {vuelta.blindaje.estados
                    .filter((e) => (e.faltan?.length ?? 0) > 0)
                    .sort((a, b) => (b.faltan?.length ?? 0) - (a.faltan?.length ?? 0))
                    .map((e) => (
                      <div className="fallo" key={e.dominio}>
                        <div className="t">
                          <span className="pill mal">{e.faltan?.length}</span> {e.dominio}
                        </div>
                        <div className="s">{(e.faltan ?? []).join(" · ")}</div>
                        {e.actualizaciones && (
                          <div className="s">Pendiente de actualizar: {e.actualizaciones}</div>
                        )}
                      </div>
                    ))}

                  {/*
                    * Las que SOLO tienen actualizaciones van contadas, no listadas.
                    *
                    * Son casi setenta. Listarlas aqui seria la pared de filas sin
                    * jerarquia que este panel evita a proposito: lo urgente es lo
                    * que le FALTA a una web, no que un plugin tenga version nueva.
                    * El detalle, web por web, va a la ficha de su cliente el dia 1.
                    */}
                  {(() => {
                    const soloActualizar = vuelta.blindaje.estados.filter(
                      (e) => (e.faltan?.length ?? 0) === 0 && e.actualizaciones,
                    ).length;
                    return soloActualizar > 0 ? (
                      <div className="nota">
                        Y {soloActualizar} web(s) al día de blindaje tienen actualizaciones
                        pendientes. El detalle va a la ficha de su cliente el día 1 de cada mes.
                      </div>
                    ) : null;
                  })()}

                  {/*
                    * Sin informe NO se pinta «todo bien».
                    *
                    * Cero webs con faltas y cero webs miradas dan el mismo cero,
                    * y significan lo contrario. Es exactamente el fallo de
                    * aparteshop: una verificacion que no miro nada salio como
                    * «0 paginas mal», y se leyo como que estaba bien.
                    */}
                  {vuelta.blindaje.resumen.total === 0 ? (
                    <div className="fallo">
                      <div className="t">El inspector no ha dejado ningun informe</div>
                      <div className="s">
                        Esto no es «las webs estan bien»: es que nadie las ha mirado. Si acaba de
                        instalarse, la primera ronda tarda unos minutos.
                      </div>
                    </div>
                  ) : (
                    vuelta.blindaje.resumen.conFaltas === 0 && (
                      <div className="todo-bien">
                        Todas las webs miradas tienen el juego completo puesto.
                      </div>
                    )
                  )}
                </section>
              )}

              {/* Lo que NO se vigila, dicho aquí y no escondido en el código. */}
              <section className="caja no-se-mide">
                <header>
                  <h2>Lo que NO se está vigilando</h2>
                </header>
                {vuelta.loQueNoSeVigila.map((x) => (
                  <div className="fallo" key={x.que}>
                    <div className="t">{x.que}</div>
                    <div className="s">{x.porque}</div>
                  </div>
                ))}
              </section>
            </>
          )}
        </>
      )}

      {cara === "cerebro" && (
        <>
      {/* ── El cerebro del director ──
       *
       * Un solo modelo y cuatro marchas, no cuatro modelos. Cambiar de modelo
       * según la pregunta le cambiaría la voz a media conversación; lo que
       * cambia es cuánto razona. El barato existe para otra cosa: fontanería
       * que no habla contigo.
       */}
      <section className="caja cerebro-caja">
        <header>
          <h2>Con qué piensa el director</h2>
          <p className="ayuda">
            Es siempre el mismo cerebro: lo que cambia es la marcha. En automático la elige
            BILBOBOT según lo que le pidas y lo que vaya encontrando. Tiene efecto en el
            mensaje siguiente, sin reiniciar nada.
          </p>
        </header>

        <div className="cerebro-modelos">
          <label>
            <span>Director</span>
            <select
              value={sistema.cerebro.modeloDirector}
              disabled={ocupado}
              onChange={(e) => void cambiarCerebro({ modeloDirector: e.target.value })}
            >
              {modelosDelProveedor.map((c) => (
                <option key={c.modelo} value={c.modelo} title={c.nota}>
                  {c.etiqueta}
                </option>
              ))}
            </select>
            <small>El que habla contigo, en las cuatro marchas.</small>
          </label>

          <label>
            <span>Procesos económicos</span>
            <select
              value={sistema.cerebro.modeloEconomico}
              disabled={ocupado}
              onChange={(e) => void cambiarCerebro({ modeloEconomico: e.target.value })}
            >
              {modelosDelProveedor.map((c) => (
                <option key={c.modelo} value={c.modelo} title={c.nota}>
                  {c.etiqueta}
                </option>
              ))}
            </select>
            <small>Clasificar, extraer de correos, titular. No habla contigo.</small>
          </label>

          <div className="proveedor-fila">
            {(["deepseek", "openai", "anthropic", "together"] as const).map((p) => (
              <button
                key={p}
                className={sistema.cerebro.proveedor === p ? "proveedor elegido" : "proveedor"}
                disabled={ocupado}
                onClick={() => void cambiarCerebro({ proveedor: p })}
              >
                <span className="nom">{NOMBRE_PROVEEDOR[p]}</span>
                {/* Sin clave, elegirlo no sirve de nada. Decirlo aquí evita el
                    paseo de cambiarlo, escribir un mensaje y recibir un 401. */}
                <span className="sub">
                  {sistema.cerebro.claves[p] ? "clave puesta" : "sin clave todavía"}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* ── Quién elige la marcha ── */}
        <div className="modo-fila">
          {(["automatico", "manual"] as const).map((m) => (
            <button
              key={m}
              className={sistema.cerebro.modo === m ? "modo elegido" : "modo"}
              disabled={ocupado}
              onClick={() => void cambiarCerebro({ modo: m })}
            >
              {m === "automatico" ? "Automático" : "Manual"}
              <span className="sub">
                {m === "automatico"
                  ? "BILBOBOT elige la marcha en cada mensaje"
                  : "todos los mensajes en la marcha que fijes"}
              </span>
            </button>
          ))}
        </div>

        <div className="marchas">
          {sistema.cerebro.niveles.map((n) => {
            const fijada = sistema.cerebro.modo === "manual" && sistema.cerebro.nivelManual === n.valor;
            return (
              <div className={fijada ? "marcha fijada" : "marcha"} key={n.valor}>
                <div className="cabecera">
                  <span className="t">{n.titulo}</span>
                  <span className="s">{n.ejemplo}</span>
                </div>

                <label>
                  <span>Cuánto piensa</span>
                  <select
                    value={String(sistema.cerebro[n.campo])}
                    disabled={ocupado}
                    onChange={(e) => void cambiarCerebro({ [n.campo]: e.target.value })}
                  >
                    {sistema.cerebro.esfuerzos.map((e) => (
                      <option key={e.valor} value={e.valor} title={e.nota}>
                        {e.etiqueta}
                      </option>
                    ))}
                  </select>
                </label>

                {/**
                 * El modelo de ESTA marcha.
                 *
                 * Lo normal es dejarlo en «el del director»: un solo cerebro
                 * mantiene una sola voz. Está aquí para poder probar —«¿aguanta
                 * Flash el chat normal?»— sin tener que cambiar el director
                 * entero y quedarte sin el bueno para lo difícil.
                 */}
                <label>
                  <span>Modelo</span>
                  <select
                    value={sistema.cerebro.modeloPorMarcha[n.valor] ?? ""}
                    disabled={ocupado}
                    onChange={(e) =>
                      void cambiarCerebro({
                        modeloPorMarcha: { [n.valor]: e.target.value || null },
                      })
                    }
                  >
                    <option value="">El del director</option>
                    {modelosDelProveedor.map((c) => (
                      <option key={c.modelo} value={c.modelo} title={c.nota}>
                        {c.etiqueta}
                      </option>
                    ))}
                  </select>
                </label>

                {/* En manual hay que poder decir CUÁL, no sólo que es manual. */}
                {sistema.cerebro.modo === "manual" && (
                  <button
                    className={fijada ? "fijar puesta" : "fijar"}
                    disabled={ocupado || fijada}
                    onClick={() => void cambiarCerebro({ nivelManual: n.valor })}
                  >
                    {fijada ? "Fijada aquí" : "Usar siempre esta"}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* Se avisa, pero no se bloquea: una combinación rara puede ser justo
            la que quieres probar. */}
        {sistema.cerebro.pegas.map((p) => (
          <p className="pega" key={p}>
            {p}
          </p>
        ))}

        <p className="nota">
          En automático puedes subir de marcha sin tocar nada: escribe «piénsalo bien» o «con
          calma» y ese mensaje va al máximo.
        </p>
      </section>

      {/* ── Ficha técnica ──
       *
       * Lo que viaja de verdad en el cuerpo de cada petición. Sale de las
       * mismas funciones que hacen la llamada, así que no puede desviarse:
       * escribirlo a mano habría durado hasta el primer cambio, y un panel que
       * dice «temperatura 1.0» mientras el código manda otra cosa es peor que
       * uno que no dice nada, porque a ese sí se le hace caso.
       */}
      <section className="caja">
        <header>
          <h2>Qué se le manda exactamente</h2>
          <p className="ayuda">
            Tal y como viaja en la petición a {NOMBRE_PROVEEDOR[sistema.cerebro.proveedor]}.
          </p>
        </header>

        <div className="tabla-envuelta">
          <table className="consumo ficha">
            <thead>
              <tr>
                <th>Caso</th>
                <th>Modelo</th>
                <th>Parámetros</th>
              </tr>
            </thead>
            <tbody>
              {sistema.cerebro.ficha.marchas.map((m) => (
                <tr key={m.nivel}>
                  <td>
                    <span className="modelo">{m.titulo}</span>
                    <span className="prov">esfuerzo: {m.esfuerzo}</span>
                  </td>
                  <td>
                    <code>{m.modelo}</code>
                  </td>
                  <td>
                    <code className="json">{JSON.stringify(m.envia)}</code>
                  </td>
                </tr>
              ))}
              <tr>
                <td>
                  <span className="modelo">Procesos económicos</span>
                  <span className="prov">no habla contigo nunca</span>
                </td>
                <td>
                  <code>{sistema.cerebro.ficha.fondo.modelo}</code>
                </td>
                <td>
                  <code className="json">{JSON.stringify(sistema.cerebro.ficha.fondo.envia)}</code>
                </td>
              </tr>
              <tr>
                <td>
                  <span className="modelo">Verificador</span>
                  <span className="prov">decide si un trabajo está hecho</span>
                </td>
                <td>
                  <code>{sistema.cerebro.ficha.verificador.modelo}</code>
                </td>
                <td>
                  <span className="tenue">esfuerzo {sistema.cerebro.ficha.verificador.esfuerzo}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Lo que no depende de la marcha: los topes y las reglas fijas. */}
        <dl className="siempre">
          {sistema.cerebro.ficha.siempre.map((x) => (
            <div key={x.que}>
              <dt>{x.que}</dt>
              <dd>{x.valor}</dd>
            </div>
          ))}
        </dl>
      </section>

        </>
      )}

      {cara === "consumo" && (
        <>
      {/* ── Consumo de IA ──
       *
       * El total ya sale arriba. Lo que se viene a mirar aquí es el porcentaje
       * de caché: con un token cacheado costando 120 veces menos que uno nuevo,
       * ese número es la diferencia entre pagar tres euros al mes o trescientos.
       */}
      <section className="caja">
        <header>
          <h2>Consumo de IA este mes</h2>
        </header>

        {sistema.consumoIa.length === 0 ? (
          <p className="vacio2">Todavía no se ha llamado a ningún modelo este mes.</p>
        ) : (
          <div className="tabla-envuelta">
            <table className="consumo">
              <thead>
                <tr>
                  <th>Modelo</th>
                  <th className="num">Llamadas</th>
                  <th className="num">Caché</th>
                  <th className="num">Entrada</th>
                  <th className="num">Salida</th>
                  <th className="num">Pensando</th>
                  <th className="num">Media</th>
                  <th className="num">Coste</th>
                </tr>
              </thead>
              <tbody>
                {sistema.consumoIa.map((f) => (
                  <tr key={`${f.proveedor}/${f.modelo}`}>
                    <td>
                      <span className="modelo">{f.modelo}</span>
                      <span className="prov">{f.proveedor}</span>
                    </td>
                    <td className="num">
                      {f.llamadas}
                      {f.fallos > 0 && <span className="pill mal">{f.fallos} mal</span>}
                    </td>
                    {/**
                     * El número que decide la factura.
                     *
                     * Una raya no es un cero: quiere decir que ese modelo no
                     * informa de caché, no que no esté aprovechándolo.
                     */}
                    <td className="num">
                      {f.porcentajeCache === null ? (
                        <span className="tenue">—</span>
                      ) : (
                        <span className={f.porcentajeCache >= 50 ? "cache bien" : "cache flojo"}>
                          {f.porcentajeCache} %
                        </span>
                      )}
                    </td>
                    <td className="num">{miles(f.cacheHit + f.cacheMiss)}</td>
                    <td className="num">{miles(f.salida)}</td>
                    <td className="num">
                      {f.razonamiento > 0 ? miles(f.razonamiento) : <span className="tenue">—</span>}
                    </td>
                    <td className="num">{f.segundosMedio.toFixed(1)} s</td>
                    <td className="num">{f.eur.toFixed(2)} €</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/**
         * El aviso que convierte la cifra en algo accionable.
         *
         * Un porcentaje suelto no dice qué hacer con él. Esto sí.
         */}
        {sistema.consumoIa.some((f) => f.porcentajeCache !== null && f.porcentajeCache < 40) && (
          <p className="nota">
            Hay un modelo por debajo del 40 % de caché. En conversaciones normales eso suele
            querer decir que algo variable —una fecha, un identificador— se está colando al
            principio del contexto y rompe la reutilización.
          </p>
        )}
      </section>

        </>
      )}
    </div>
  );
}
/**
 * El A- / A+ de la letra, en la cabecera del panel.
 *
 * Aquí y no en Ajustes porque es donde se mira cuando algo no se lee: se sube
 * y se sigue leyendo en la misma pantalla. Se guarda en el aparato, así que el
 * móvil puede ir más grande que el escritorio sin tocar el otro.
 */
function TamanoDeLetra() {
  const [escala, setEscala] = useState(escalaGuardada);
  const mover = (hacia: "mas" | "menos") => setEscala(ponerEscala(siguienteEscala(escala, hacia)));
  const enElTope = (hacia: "mas" | "menos") => siguienteEscala(escala, hacia) === escala;

  return (
    <div className="tamano-letra" role="group" aria-label="Tamaño de la letra">
      <button
        className="acc"
        onClick={() => mover("menos")}
        disabled={enElTope("menos")}
        aria-label="Letra más pequeña"
        title="Letra más pequeña"
      >
        A−
      </button>
      {/*
        * El número dice en qué punto está.
        *
        * Sin él, quien haya llegado al tope no sabe si el botón no hace nada
        * porque está deshabilitado o porque la app se ha quedado colgada.
        */}
      <span className="cuanto" aria-hidden="true">
        {Math.round(escala * 100)}%
      </span>
      <button
        className="acc"
        onClick={() => mover("mas")}
        disabled={enElTope("mas")}
        aria-label="Letra más grande"
        title="Letra más grande"
      >
        A+
      </button>
    </div>
  );
}


